import { brand } from '../../../ui/theme/tokens';

/**
 * Shared tokens for the Settings UI, sourced from the Yaxle brand scale
 * (src/ui/theme/tokens) so Settings speaks the same Apple Blue as the rest of
 * the app. The names keep their historical spelling because the whole
 * settings tree reads them.
 */

export const PARTBAY_BLUE = brand[500];
export const PARTBAY_BLUE_HOVER = brand[600];
export const PARTBAY_BLUE_TINT = brand[50];
/** A step lighter than the tint, for a panel that groups tinted controls. */
export const PARTBAY_BLUE_WASH = '#F5F9FE';

/** Max width for non-table content, centered between sidebar and right edge. */
export const SETTINGS_CONTENT_MAX_WIDTH = '900px';

/** Horizontal padding for table-variant toolbars, matching the list pages. */
export const SETTINGS_TABLE_GUTTER = 6;

export const SETTINGS_BORDER_COLOR = 'gray.200';

/**
 * Hover treatment for a click-to-edit table cell. At rest the cell reads as
 * plain text; hovering it draws an input's outline so it is obvious the value
 * can be changed in place. The border is always there (transparent at rest) so
 * hovering never nudges row heights.
 *
 * Spread onto the clickable element inside a cell, together with
 * EDITABLE_CELL_GEOMETRY so every editable cell lines up the same way.
 */
export const EDITABLE_CELL_HOVER = {
    borderRadius: 'md',
    borderWidth: '1px',
    borderColor: 'transparent',
    transition: 'background 0.12s ease, border-color 0.12s ease',
    // Yaxle keeps shadows for overlays; the drawn border is the affordance.
    _hover: {
        bg: 'white',
        borderColor: 'gray.300',
    },
} as const;

/**
 * Padding plus matching negative margin, so the outline stretches out to the
 * cell's gridlines instead of only wrapping the text. The explicit width is
 * what makes that work: buttons and inline-flex labels size to their content,
 * so `width: auto` alone would leave the outline hugging the value.
 */
export const EDITABLE_CELL_GEOMETRY = {
    px: 2,
    py: 1,
    mx: -2,
    my: -1,
    w: 'calc(100% + 1rem)',
} as const;

/** Applied instead of the hover state while a cell's editor is open. */
export const EDITABLE_CELL_ACTIVE = {
    bg: 'white',
    borderColor: PARTBAY_BLUE,
    boxShadow: `0 0 0 1px ${PARTBAY_BLUE}`,
} as const;

/**
 * A field and its trailing unit addon ("%", "days") read as one control: the
 * seam they share is square on both sides, and only the outer corners round.
 *
 * Every corner is named explicitly, and no `borderRadius` shorthand is passed
 * alongside them. Chakra's `css()` walks style keys in insertion order and
 * writes a longhand into the slot its first writer claimed, so a shorthand
 * declared *before* `borderRightRadius={0}` still serialises *after* it and
 * quietly re-rounds the edge — which is what put a curve against the addon's
 * flat side. Four longhands leave nothing to reorder.
 */
export const UNIT_FIELD_INPUT_RADII = {
    borderTopLeftRadius: 'md',
    borderBottomLeftRadius: 'md',
    borderTopRightRadius: 0,
    borderBottomRightRadius: 0,
} as const;

/** The addon half of {@link UNIT_FIELD_INPUT_RADII}. */
export const UNIT_FIELD_ADDON_RADII = {
    borderTopLeftRadius: 0,
    borderBottomLeftRadius: 0,
    borderTopRightRadius: 'md',
    borderBottomRightRadius: 'md',
} as const;

/** A unit-less field keeps all four corners rounded. */
export const FIELD_RADII = {
    borderTopLeftRadius: 'md',
    borderBottomLeftRadius: 'md',
    borderTopRightRadius: 'md',
    borderBottomRightRadius: 'md',
} as const;
